import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import styles from "./catalog.module.css";

/**
 * DUPLICATE SHELVES
 *
 * The phase-1 backfill created one shelf per distinct natural key and
 * deliberately refused to merge the rows that shared one — "duplicate product"
 * clones, a plan typed twice, a soft-deleted twin of a live row. Merging them
 * would retire a row that might be carrying live subscriptions, and that is an
 * admin decision, not a script's.
 *
 * Two ways out, and nothing is ever deleted:
 *
 *   RETIRE  the shelf stops taking new quotes and disappears from the vendor
 *           portal. Its offers keep serving every subscription already on
 *           them, at the price those were sold.
 *   MERGE   the shelf's offers move onto another shelf, keeping their ids and
 *           their tiers' ids, and the emptied shelf is retired. Refused when
 *           it would give one vendor two offers on the target — the one thing
 *           the model forbids — and the refusal says which vendor.
 *
 * On the current data most duplicates are the SAME vendor twice (the house
 * cloning a product), so merge is impossible by definition and retire is the
 * answer. The screen says so before you click rather than after.
 */

const API = process.env.REACT_APP_SERVER_URL;

const vendorKeyOf = (v) => (v === null || v === undefined ? "null" : String(Number(v)));

function DuplicateShelves() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [merge, setMerge] = useState(null); // { group, sourceSlotId, targetSlotId }
  const [retire, setRetire] = useState(null); // { group, shelf }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/catalog/duplicate-shelves`, {
        credentials: "include",
      });
      const body = await res.json();
      if (!body.success) {
        toast.error(body.msg || "Failed to load duplicate shelves");
        setGroups([]);
      } else {
        setGroups(body.groups || []);
      }
    } catch {
      toast.error("Failed to load duplicate shelves");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) =>
      [g.product_name, g.plan_name, g.interval, g.region, g.currency]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [groups, query]);

  // The clash the server will refuse, worked out before the click: a vendor
  // holding an offer on both shelves.
  const clashingVendors = (group, sourceId, targetId) => {
    const source = group.shelves.find((s) => s.slot_id === Number(sourceId));
    const target = group.shelves.find((s) => s.slot_id === Number(targetId));
    if (!source || !target) return [];
    const targetKeys = new Set(target.offers.map((o) => vendorKeyOf(o.vendor_id)));
    return source.offers
      .filter((o) => targetKeys.has(vendorKeyOf(o.vendor_id)))
      .map((o) => o.vendor_name);
  };

  const doMerge = async () => {
    if (!merge) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/catalog/shelves/merge`, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          source_slot_id: Number(merge.sourceSlotId),
          target_slot_id: Number(merge.targetSlotId),
        }),
      });
      const body = await res.json();
      if (!body.success) {
        toast.error(body.msg || "Failed to merge");
        setBusy(false);
        return;
      }
      toast.success(body.msg);
      setMerge(null);
      await load();
    } catch {
      toast.error("Failed to merge");
    }
    setBusy(false);
  };

  const doRetire = async (shelf, status) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/catalog/shelves/${shelf.slot_id}/status`, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status }),
      });
      const body = await res.json();
      if (!body.success) {
        toast.error(body.msg || "Failed to change the shelf");
        setBusy(false);
        return;
      }
      toast.success(body.msg);
      setRetire(null);
      await load();
    } catch {
      toast.error("Failed to change the shelf");
    }
    setBusy(false);
  };

  const mergeClash = merge
    ? clashingVendors(merge.group, merge.sourceSlotId, merge.targetSlotId)
    : [];

  return (
    <div className={styles.Page}>
      <div className={styles.PageHead}>
        <div>
          <h1 className={styles.PageTitle}>Duplicate shelves</h1>
          <p className={styles.PageSub}>
            Plans on the same product that are the same plan — same type, name,
            billing period, region and currency. Retire the spare, or merge its
            offers into the one you are keeping. Nothing is deleted and no
            subscription moves either way.
          </p>
        </div>
        <div className={styles.HeadActions}>
          <TextField
            size="small"
            placeholder="Search product or plan"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Button variant="outlined" size="small" onClick={load} disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className={styles.Loading}>
          <CircularProgress size={26} />
        </div>
      ) : !visible.length ? (
        <p className={styles.Empty}>
          No duplicate shelves. Every plan on every product has one shelf.
        </p>
      ) : (
        <div className={styles.List}>
          {visible.map((g) => (
            <div key={g.key} className={styles.Card}>
              <div className={styles.CardHead}>
                <div className={styles.CardTitleBlock}>
                  <div className={styles.CardTitle}>{g.plan_name || "(unnamed plan)"}</div>
                  <div className={styles.CardMeta}>
                    <strong>{g.product_name}</strong>
                    <span>·</span>
                    <span>type #{g.type_id}</span>
                    <span>·</span>
                    <span>
                      {g.n_interval} {g.interval}
                    </span>
                    <span>·</span>
                    <span>{g.region || "all regions"}</span>
                    <span>·</span>
                    <span>{g.currency}</span>
                  </div>
                </div>
                <Chip size="small" color="warning" label={`${g.count} shelves`} />
              </div>

              <table className={styles.Table}>
                <thead>
                  <tr>
                    <th>Shelf</th>
                    <th>Status</th>
                    <th>Offers</th>
                    <th>Tiers</th>
                    <th>Subscriptions</th>
                    <th>Sells</th>
                    <th>Vendors</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {g.shelves.map((s) => (
                    <tr
                      key={s.slot_id}
                      className={
                        s.slot_id === g.suggested_keeper_slot_id ? styles.Keeper : undefined
                      }
                    >
                      <td>
                        #{s.slot_id}
                        {s.slot_id === g.suggested_keeper_slot_id ? (
                          <span className={styles.KeepTag}>suggested keeper</span>
                        ) : null}
                      </td>
                      <td>{s.status}</td>
                      <td>{s.offer_count}</td>
                      <td>{s.tiers}</td>
                      <td className={s.subscriptions ? styles.Warn : undefined}>
                        {s.subscriptions}
                      </td>
                      <td>{s.sells ? "yes" : "—"}</td>
                      <td>{s.offers.map((o) => o.vendor_name).join(", ") || "—"}</td>
                      <td className={styles.RowEnd}>
                        {s.status === "retired" ? (
                          <Button
                            size="small"
                            disabled={busy}
                            onClick={() => doRetire(s, "active")}
                          >
                            Restore
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            color="warning"
                            disabled={busy}
                            onClick={() => setRetire({ group: g, shelf: s })}
                          >
                            Retire
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className={styles.CardActions}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    setMerge({
                      group: g,
                      sourceSlotId: String(
                        g.shelves.find((s) => s.slot_id !== g.suggested_keeper_slot_id)
                          ?.slot_id ?? g.shelves[0].slot_id,
                      ),
                      targetSlotId: String(g.suggested_keeper_slot_id ?? g.shelves[0].slot_id),
                    })
                  }
                >
                  Merge two of these
                </Button>
                <span className={styles.Muted}>
                  Suggested keeper is the shelf with live subscriptions, then the
                  one that sells, then the one with the most prices.
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── merge ── */}
      <Dialog open={Boolean(merge)} onClose={() => (busy ? null : setMerge(null))} fullWidth maxWidth="sm">
        <DialogTitle>Merge one shelf into another</DialogTitle>
        <DialogContent>
          <p className={styles.DialogNote}>
            Every offer on the source moves to the target, keeping its id and
            its tiers' ids, so every live subscription resolves to the same row
            at the same price. The emptied shelf is retired, not deleted.
          </p>
          <div className={styles.MergeRow}>
            <div>
              <span className={styles.FieldLabel}>Move this shelf</span>
              <Select
                fullWidth
                size="small"
                value={merge?.sourceSlotId || ""}
                onChange={(e) =>
                  setMerge((m) => ({ ...m, sourceSlotId: e.target.value }))
                }
              >
                {(merge?.group.shelves || []).map((s) => (
                  <MenuItem key={s.slot_id} value={String(s.slot_id)}>
                    #{s.slot_id} — {s.offer_count} offer{s.offer_count === 1 ? "" : "s"},{" "}
                    {s.subscriptions} sub{s.subscriptions === 1 ? "" : "s"}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <div>
              <span className={styles.FieldLabel}>Into this one</span>
              <Select
                fullWidth
                size="small"
                value={merge?.targetSlotId || ""}
                onChange={(e) =>
                  setMerge((m) => ({ ...m, targetSlotId: e.target.value }))
                }
              >
                {(merge?.group.shelves || []).map((s) => (
                  <MenuItem key={s.slot_id} value={String(s.slot_id)}>
                    #{s.slot_id} — {s.offer_count} offer{s.offer_count === 1 ? "" : "s"},{" "}
                    {s.subscriptions} sub{s.subscriptions === 1 ? "" : "s"}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>

          {merge && merge.sourceSlotId === merge.targetSlotId ? (
            <p className={styles.DialogWarn}>Pick two different shelves.</p>
          ) : mergeClash.length ? (
            <p className={styles.DialogWarn}>
              Cannot merge: {[...new Set(mergeClash)].join(", ")} already{" "}
              {mergeClash.length === 1 ? "has an offer" : "have offers"} on the
              target, and a vendor may hold only one offer per shelf. Retire one
              of the two shelves instead.
            </p>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMerge(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={doMerge}
            disabled={
              busy ||
              !merge ||
              merge.sourceSlotId === merge.targetSlotId ||
              mergeClash.length > 0
            }
          >
            {busy ? "Working…" : "Merge"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── retire ── */}
      <Dialog open={Boolean(retire)} onClose={() => (busy ? null : setRetire(null))} fullWidth maxWidth="sm">
        <DialogTitle>Retire shelf #{retire?.shelf.slot_id}?</DialogTitle>
        <DialogContent>
          <p className={styles.DialogNote}>
            It stops taking new quotes and disappears from the vendor portal and
            from the buyer grid. Nothing is deleted:{" "}
            {retire?.shelf.subscriptions
              ? `the ${retire.shelf.subscriptions} subscription${
                  retire.shelf.subscriptions === 1 ? "" : "s"
                } already on it keep renewing at the price they were sold.`
              : "its offers and prices stay exactly where they are."}
          </p>
          {retire?.shelf.sells ? (
            <p className={styles.DialogWarn}>
              This is the shelf that currently sells. Check the one you are
              keeping has a seller afterwards.
            </p>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRetire(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => doRetire(retire.shelf, "retired")}
            disabled={busy}
          >
            {busy ? "Working…" : "Retire"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default DuplicateShelves;
